import { derived, writable, type Readable, type Writable } from 'svelte/store';

export function createDebouncedValue<T>(initialValue: T, ms = 500): [Writable<T>, Readable<T>] {
  const value = writable(initialValue);
  let timeout: NodeJS.Timeout;
  const debounced = derived(
    value,
    ($value, set) => {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => set($value), ms);
      return () => clearTimeout(timeout);
    },
    initialValue
  );

  return [value, debounced];
}

export function throttleStore<T>(
  store: Readable<T>,
  delay: number
): Readable<T> & { unsubscribe: () => void } {
  const throttled: Writable<T> = writable<T>(undefined as unknown as T);

  let timeout: NodeJS.Timeout | null = null;
  let lastValue: T;

  const unsubscribe = store.subscribe((value: T) => {
    lastValue = value;

    if (!timeout) {
      throttled.set(lastValue);
      timeout = setTimeout(() => {
        throttled.set(lastValue);
        timeout = null;
      }, delay);
    }
  });

  return {
    subscribe: throttled.subscribe,
    unsubscribe // Return unsubscribe for manual cleanup
  };
}

export function createInterval(callback: () => void, milliseconds: number | undefined): () => void {
  const interval = setInterval(callback, milliseconds);
  return () => clearInterval(interval); // Return a cleanup function
}

export function createTimeout(callback: () => void, milliseconds: number | undefined): () => void {
  const timeout = setTimeout(callback, milliseconds);
  return () => clearTimeout(timeout); // Return a cleanup function
}
